export type Bc3Concept = {
  code: string; unit: string; summary: string; description: string;
  prices: (number | null)[]; dates: string[]; type: string; chapter: boolean;
};
export type Bc3Link = { parent: string; child: string; factor: number | null; yield: number | null; percentageCodes?:string[] };
export type Bc3Preview = { concepts: Bc3Concept[]; links: Bc3Link[]; warnings: string[]; version: string; owner: string; encoding: string; decodedEncoding?:string; recordTypes: Record<string, number> };
const canonical = (value: string) => value.trim().replace(/#+$/, "");
function number(value: string, fallback: number | null = null): number | null {
  if (!value.trim()) return fallback;
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}
export function parseBc3(text: string): Bc3Preview {
  if (text.length > 8 * 1024 * 1024) throw new Error("O ficheiro excede o limite de 8 MB.");
  const records = text.replace(/^\uFEFF/, "").replace(/\x1a/g, "").split("~").slice(1);
  if (!records.length) throw new Error("O ficheiro não contém registos BC3.");
  const concepts = new Map<string, Bc3Concept>();
  const descriptions = new Map<string, string>();
  const links: Bc3Link[] = [];
  const compositionParents = new Set<string>();
  const warnings = new Set<string>();
  const recordTypes: Record<string, number> = {};
  let version = ""; let owner = ""; let encoding = "";
  for (const record of records) {
    const fields = record.trim().split("|");
    const kind = fields[0].trim();
    recordTypes[kind] = (recordTypes[kind] ?? 0) + 1;
    if (kind === "V") { owner = fields[1]?.trim() ?? ""; version = fields[2]?.trim() ?? ""; encoding = fields[5]?.trim() ?? "850"; }
    else if (kind === "C") {
      const aliases = (fields[1] ?? "").split("\\").filter(Boolean);
      const rawCode = aliases[0]?.trim() ?? ""; const code = canonical(rawCode);
      if (!code) { warnings.add("Existe um conceito sem código."); continue; }
      if (concepts.has(code)) { warnings.add("Código repetido: " + code + ". Foi preservada a primeira definição."); continue; }
      if (aliases.length > 1) warnings.add("Existem códigos alternativos; a pré-visualização usa o primeiro.");
      const priceValues = (fields[4] ?? "").split("\\"); if (priceValues.at(-1) === "") priceValues.pop();
      const prices = priceValues.length ? priceValues.map((value) => number(value)) : [null];
      if (prices.some((price) => price === null) && !rawCode.endsWith("#") && !/[%&]/.test(code)) warnings.add("Existem preços em falta ou inválidos.");
      if (prices.length > 1) warnings.add("Existem várias tarifas; todas são preservadas, sem escolher automaticamente uma.");
      concepts.set(code, { code, unit: fields[2]?.trim() ?? "", summary: fields[3]?.trim() ?? "", description: "",
        prices, dates: (fields[5] ?? "").split("\\").filter(Boolean), type: fields[6]?.trim() ?? "", chapter: rawCode.endsWith("#") });
    } else if (kind === "T") descriptions.set(canonical(fields[1] ?? ""), fields[2] ?? "");
    else if (kind === "D" || kind === "Y") {
      const parent = canonical(fields[1] ?? "");
      const modern=!!fields[3]?.trim();
      const entries=(modern ? fields[3] : fields[2] ?? "").split("\\");
      if(entries.at(-1)==="")entries.pop();
      const width=modern ? 4 : 3;
      if(entries.length%width!==0){warnings.add("Composição com campos adicionais ou estrutura não suportada: "+parent);continue;}
      if(kind==="D" && compositionParents.has(parent))warnings.add("Composição repetida: "+parent);
      if(entries.length)compositionParents.add(parent);
      for(let i=0;i<entries.length;i+=width){
        const child=canonical(entries[i]);if(!child)continue;
        const factor=number(entries[i+1],1);const yieldValue=number(entries[i+2],1);
        const percentageCodes=modern ? entries[i+3].split(";").map(canonical).filter(Boolean) : [];
        if(percentageCodes.length)warnings.add("Base percentual explícita ainda não suportada: "+parent);
        if(factor===null || yieldValue===null)warnings.add("Consumo inválido em "+parent+" → "+child);
        links.push({parent,child,factor,yield:yieldValue,...(percentageCodes.length ? {percentageCodes} : {})});
      }
    } else if (!["K", "M"].includes(kind)) warnings.add("Registo " + kind + " preservado no original; ainda não interpretado.");
  }
  for (const concept of concepts.values()) concept.description = descriptions.get(concept.code) ?? "";
  for (const link of links) {
    if (!concepts.has(link.parent) || !concepts.has(link.child)) warnings.add("Referência sem conceito definido: " + link.parent + " → " + link.child);
    if (link.child.includes("%") || link.child.includes("&")) warnings.add("Há recursos percentuais: a base de cálculo precisa de validação específica.");
  }
  const adjacency = new Map<string, string[]>();
  for (const link of links) {
    const items=adjacency.get(link.parent);
    if(items)items.push(link.child);else adjacency.set(link.parent,[link.child]);
  }
  const visiting = new Set<string>(); const visited = new Set<string>();
  function visit(code: string, depth: number) {
    if (visiting.has(code)) { warnings.add("Ciclo detetado na hierarquia: " + code); return; }
    if (visited.has(code)) return;
    if (depth > 100) { warnings.add("Hierarquia excede 100 níveis."); return; }
    visiting.add(code); for (const child of adjacency.get(code) ?? []) visit(child, depth+1);
    visiting.delete(code); visited.add(code);
  }
  for (const code of concepts.keys()) visit(code, 0);
  if (recordTypes.M) warnings.add("O ficheiro contém medições; não são aplicadas a propostas nesta pré-visualização.");
  if (!concepts.size) throw new Error("Não foram encontrados conceitos (~C).");
  return { concepts: [...concepts.values()], links, warnings: [...warnings], version, owner, encoding, recordTypes };
}
